import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { SmlsModal, SmlsButton, SmlsInputText, SmlsContact } from '@smiles/smiles-ui-kit-react';
import { Helpers as AccountHelpers } from '@smiles/core-account';
import { useTranslation } from 'react-i18next';
import { loginDataLayer } from 'utils/dataLayer';
import { validPhoneNumber } from '@smiles/smiles-ui-kit-react/dist/components/Contact/types';
import { CountriesListType } from '../../services/core/types';
import { getListCountry, getMemberInfo } from '../../services/core/services';
import {
  isValidMemberNumberOrCPF,
  isValidEmail,
  isValidName,
  isValidPhoneNumber,
  validate,
  formatMask,
} from '../../services/core/helpers';

type FormInputs = {
  identifier: string;
  name: string;
  email: string;
  cellPhone: {
    DDI: string;
    DDD: string;
    number: string;
  };
};
interface Props {
  isOpen: boolean;
  isLoading: boolean;
  toggleModal(isOpen: boolean): void;
  onSubmit(data: FormInputs): void;
}

const SuspendedUserModal: React.FC<Props> = ({ isOpen = false, isLoading, toggleModal, onSubmit, children }) => {
  const [fixedHeader, setFixedHeader] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [cellPhone, setCellPhone] = useState('');
  const [formErrors, setFormErrors] = useState<any>({});
  const [listCountries, setListCountries] = useState([]);
  const [isValidPhone, setIsValidPhone] = useState(false);
  const { t } = useTranslation('suspended_account_modal');
  const modalContentRef = useRef<HTMLDivElement>(null);

  const allValidated = useMemo(() => {
    const { isValid: byPassMemberOrCPF } = isValidMemberNumberOrCPF(identifier);
    const { isValid: byPassEmail } = isValidEmail(email);
    const { isValid: byPassName } = isValidName(name);
    const { isValid: byPassContact } = isValidPhoneNumber(cellPhone);
    const isAllValid = byPassMemberOrCPF && byPassEmail && byPassName && byPassContact;
    return isAllValid;
  }, [identifier, email, name, cellPhone]);

  const loadCountries = useCallback(async () => {
    const { data } = await getListCountry();
    const countries = data as CountriesListType[];
    if (!countries || !countries.length) return;
    const list = countries.map((el: any) => ({
      ...el,
      name: el.country,
    }));
    setListCountries(list as any);
  }, []);

  const getErrorMessage = useMemo(() => {
    return {
      identifier: isValidMemberNumberOrCPF(identifier).type === 'MEMBER' ? t('form_member_error') : t('form_cpf_error'),
      name: t('form_name_error'),
      email: t('form_email_error'),
      cellPhone: t('form_phone_error'),
    } as any;
  }, [t, identifier]);

  const handleScroll = useCallback(() => {
    if (!modalContentRef.current) return;
    if (window.innerHeight > 750 || !isOpen) return;

    setFixedHeader(modalContentRef.current.getBoundingClientRect().y < -76);
  }, [modalContentRef, isOpen]);

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    const modalContentDiv = document.querySelector(
      '.smls-suspended-user > .smls-suspended-user-main-content .suspended-user-modal > div .modal-content',
    );
    if (modalContentDiv) {
      modalContentDiv.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (modalContentDiv) {
        modalContentDiv.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  const handleValidadeInput = useCallback(
    (inputName: string, value: string) => {
      const errors: any = formErrors;
      const inputWithoutMask = value.replace(/[.-]+/g, '');
      const getHelperValidation = {
        identifier: (inputValue: string) => validate(isValidMemberNumberOrCPF(inputValue)),
        name: (inputValue: string) => validate(isValidName(inputValue)),
        email: (inputValue: string) => validate(isValidEmail(inputValue)),
        cellPhone: (inputValue: string) => isValidPhoneNumber(inputValue).isValid,
      } as any;

      const isValidInput = getHelperValidation[inputName];

      if (inputName === 'identifier') {
        if (inputWithoutMask.length > 9) {
          setIdentifier(formatMask(inputWithoutMask, '000.000.000-00'));
        } else {
          setIdentifier(inputWithoutMask);
        }
      }

      if (value.toString().length < 1) {
        errors[inputName] = getErrorMessage[inputName];
        setFormErrors({ ...errors });
        return false;
      }

      if (!isValidInput(inputName !== 'email' ? inputWithoutMask : value)) {
        errors[inputName] = getErrorMessage[inputName];
        setFormErrors({ ...errors });
        return false;
      }
      delete errors[inputName];
      setFormErrors({ ...errors });
      return true;
    },
    [formErrors, getErrorMessage],
  );

  const handleSubmitForm = useCallback(
    async (e: React.FormEvent<EventTarget>) => {
      e.preventDefault();
      const arr = cellPhone.split(' ');
      const getNumber = (numbers: string) => {
        return numbers.replaceAll(/[^0-9]/gi, '');
      };
      onSubmit({
        identifier,
        name,
        email,
        cellPhone: {
          DDI: getNumber(arr[0]),
          DDD: getNumber(arr[1]),
          number: getNumber(arr[2]),
        },
      });
    },
    [identifier, name, email, cellPhone, onSubmit],
  );

  const handleOnValid = useCallback((bool: boolean) => {
    setIsValidPhone(bool);
    return bool;
  }, []);

  const handleValidateCellPhone = (value: string): validPhoneNumber => {
    const isValid = AccountHelpers.isValidPhoneNumber(value);

    if (!isValid.isValid && value.length >= 7) {
      const msg = isValid.DDD && !isValid.DDD.isValid ? t('form_ddd_error') : getErrorMessage.cellPhone;
      loginDataLayer.triggerUserErrorEvent('Valor invalido', msg);
    }

    return isValid;
  };

  const handleValidate = (inputName: string, value: string): boolean => {
    const isValid = handleValidadeInput(inputName, value);

    if (!isValid) {
      loginDataLayer.triggerUserErrorEvent('Valor invalido', getErrorMessage[inputName]);
    }

    return isValid;
  };

  const handleToggleModal = () => {
    toggleModal(!isOpen);
    window.location.href = `${window.location.origin}/home?upd=${new Date().getTime()}`;
  };

  return (
    <SmlsModal
      id="suspendedUserModal"
      isOpen={isOpen}
      defaultHeader={false}
      className="suspended-user-modal"
      type="right"
      toggle={() => {
        toggleModal(false);
      }}
      initialFocus="div"
    >
      {isOpen && (
        <div ref={modalContentRef}>
          <div className={`${fixedHeader ? 'modal-header-fixed' : 'modal-header'}`}>
            <button
              type="button"
              className="smls-btn-close"
              data-dismiss="modal"
              aria-label="Close"
              onClick={handleToggleModal}
            >
              <i className="material-icons">close</i>
            </button>
            <h4>{t('form_title')}</h4>
          </div>

          <div className="suspended-user-form-wrapper">
            <p className={fixedHeader ? 'fixed' : ''}>{t('form_subtitle')}</p>
            <form onSubmit={handleSubmitForm}>
              <SmlsInputText
                textLabel={t('form_input_member_identifier')}
                id="member_number"
                type="text"
                placeholder="000000000"
                errorMessage={getErrorMessage.identifier}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                validate={(value) => handleValidate('identifier', value)}
                debouncedTime={700}
              />
              <SmlsInputText
                textLabel={t('form_input_name')}
                id="name"
                type="text"
                placeholder="Ex: Alberto Santos Dumont"
                errorMessage={getErrorMessage.name}
                value={name}
                onChange={(e) => setName(e.target.value)}
                validate={(value) => handleValidate('name', value)}
                debouncedTime={700}
              />
              <SmlsInputText
                textLabel={t('form_input_email')}
                id="email"
                type="text"
                placeholder="Ex: exemplo@email.com"
                errorMessage={getErrorMessage.email}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                validate={(value) => handleValidate('email', value)}
                debouncedTime={700}
              />
              <SmlsContact
                listCountries={listCountries}
                isValidPhoneNumber={handleValidateCellPhone}
                textLabel={t('form_input_phone')}
                headerTitle={t('form_contact_modal_title')}
                errorMessage={cellPhone.length && !isValidPhone ? getErrorMessage.cellPhone : ''}
                errorMessageDDD={t('form_ddd_error')}
                value={cellPhone}
                onChange={(phoneNumber) => setCellPhone(phoneNumber)}
                isValid={handleOnValid}
              />
              <SmlsButton
                text={t('form_submit_button')}
                color="primary"
                type="submit"
                isLoading={isLoading}
                disabled={!allValidated}
              />
            </form>
          </div>
        </div>
      )}
      {children}
    </SmlsModal>
  );
};

export default SuspendedUserModal;
